import { useCallback, useMemo, useState } from "react";
import * as z from "zod";

export interface EditableStoreApi<Schema extends z.ZodType> {
	validate(item?: z.input<Schema>): void;
	parse(): z.ZodSafeParseResult<Array<z.output<Schema>>>;

	resetItem(id: string): void;
	resetAll(): void;

	setPristine: Setter<Array<z.input<Schema>>>;
	setDirty: (entity: z.input<Schema>) => void;
	setRemoved: (id: string) => void;

	values: Array<z.input<Schema>>;
	erroringIds: string[];

	itemData: (id: string) => {
		value: z.input<Schema>;
		pristineValue: z.input<Schema> | null;
		errors: { [key in keyof z.input<Schema>]?: string[] };
	} | null;
}
export function useEditableStore<Schema extends z.ZodType<{ id: string; }>>(initialValues: Array<z.input<Schema>>, schema: Schema): EditableStoreApi<Schema> {
	type T = z.input<Schema>;
	type SimpleMap<T> = Record<string, T>;

	const [pristineEntities, setPristineEntities] = useState(initialValues);
	const [dirtyEntities, setDirtyEntities] = useState<SimpleMap<T | null>>({});
	const [entityErrors, setEntityErrors] = useState<SimpleMap<{ [key in keyof T]?: string[] } | undefined>>({});

	const validate = useCallback((item?: T) => {
		let itemsToValidate: T[] = [];
		if (item) {
			itemsToValidate = [item];
		} else {
			itemsToValidate = Object.values(dirtyEntities).filter((entity): entity is T => entity !== null);
		}

		const newErrors: SimpleMap<{ [key in keyof T]?: string[] } | undefined> = {};
		for (const item of itemsToValidate) {
			const value: { [key in keyof T]?: string[] } = {};
			const itemId = (item as { id: string; }).id;	// El schema no se entera de que T tiene id. Genericos.
			newErrors[itemId] = value;

			const result = schema.safeParse(item);
			if (!result.success) {
				console.debug("Validation errors for item", itemId, result.error.issues);
				for (const issue of result.error.issues) {
					const key = issue.path[0] as keyof T;
					value[key] ??= [];
					value[key].push(issue.message);
				}
			}
		}
		setEntityErrors((prev) => ({ ...prev, ...newErrors }));
	}, [dirtyEntities, schema]);

	const resetItem = useCallback((id: string) => {
		setDirtyEntities((prev) => {
			const newDirty = { ...prev };
			delete newDirty[id];
			return newDirty;
		});
		setEntityErrors((prev) => {
			const newErrors = { ...prev };
			delete newErrors[id];
			return newErrors;
		});
	}, []);
	const resetAll = useCallback(() => {
		setDirtyEntities({});
		setEntityErrors({});
	}, []);

	const setPristine = useCallback((entities: (T[]) | ((t: T[]) => T[])) => {
		setPristineEntities((prev) => typeof entities === "function" ? entities(prev) : entities);
		//TODO Hay que ver como afecta esto a los dirtyEntities y entityErrors.
		// setDirtyEntities({});
		// setEntityErrors({});
	}, []);
	const setDirty = useCallback((entity: T) => {
		const itemId = (entity as { id: string; }).id;	// El schema no se entera de que T tiene id. Genericos.
		setDirtyEntities((prev) => ({
			...prev,
			[itemId]: entity,
		}));
		validate(entity);
	}, [validate]);
	const setRemoved = useCallback((id: string) => {
		setDirtyEntities((prev) => ({
			...prev,
			[id]: null,
		}));
		validate();
	}, [validate]);

	const valueFn = useCallback((id: string) => dirtyEntities[id] ?? findById(pristineEntities, id) ?? null, [dirtyEntities, pristineEntities]);
	const pristineFn = useCallback((id: string) => findById(pristineEntities, id) ?? null, [pristineEntities]);
	const errorsFn = useCallback((id: string) => entityErrors[id] ?? {}, [entityErrors]);

	const itemDataFn = useCallback((id: string) => {
		const value = valueFn(id);
		const pristineValue = pristineFn(id);
		const errors = errorsFn(id);
		if (value) {
			return { value, pristineValue, errors };
		}
		return null;
	}, [valueFn, pristineFn, errorsFn]);

	const allValues = useMemo(() => {
		const merged = { ...dirtyEntities };
		pristineEntities.forEach((entity) => {
			const itemId = (entity as { id: string; }).id;	// El schema no se entera de que T tiene id. Genericos.
			if (!(itemId in merged)) {
				merged[itemId] = entity;
			}
		});
		return Object.values(merged).filter((entity): entity is T => entity !== null);
	}, [pristineEntities, dirtyEntities]);

	const parse = useCallback(() => {
		const parser = z.array(schema);
		return parser.safeParse(allValues);
	}, [allValues, schema]);

	const erroringIds = useMemo(() => Object.keys(entityErrors).filter((id) => Object.keys(entityErrors[id] ?? []).length > 0), [entityErrors]);

	return {
		validate,
		parse,

		resetItem,
		resetAll,

		setPristine,
		setDirty,
		setRemoved,

		values: allValues,
		erroringIds,

		itemData: itemDataFn,
	};
}
export interface ItemData<T> {
	value: T;
	pristineValue: T;
	errors: { [key in keyof T]?: string[] };
}


function findById<T>(array: T[], id: string): T | undefined {
	return array.find((item) => (item as { id: unknown; }).id === id);
}